import type { Item, MusicAlbum } from './api.ts';

export interface MusicLibraryAlbumGroup {
  /** Grup anahtarı: albüm kimliği varsa o, yoksa sanatçı + albüm adı. */
  key: string;
  /** Albüm ekranına gitmek için albüm kimliği; eski sunucuda `null`. */
  id: string | null;
  title: string;
  year: number | null;
  coverItemId: string | null;
  coverThumbnailUrl?: string | null;
  tracks: Item[];
}

export interface MusicLibraryArtistGroup {
  key: string;
  name: string;
  coverItemId: string | null;
  coverThumbnailUrl?: string | null;
  albumCount: number;
  trackCount: number;
  albums: MusicLibraryAlbumGroup[];
}

const UNKNOWN_ARTIST = 'Bilinmeyen Sanatçı';
const UNKNOWN_ALBUM = 'Bilinmeyen Albüm';

function artistOf(item: Item): string {
  return item.music?.albumArtist ?? item.music?.artist ?? UNKNOWN_ARTIST;
}

function albumOf(item: Item): string {
  return item.music?.album ?? UNKNOWN_ALBUM;
}

function compareText(a: string, b: string): number {
  return a.localeCompare(b, 'tr');
}

interface ResolvedAlbum {
  id: string | null;
  key: string;
  title: string;
  artist: string;
  year: number | null;
  coverItemId: string;
  coverThumbnailUrl: string | null;
}

/**
 * Bir parçanın albümünü çözer: önce sunucu künyesi, yoksa parça etiketleri.
 *
 * `albumId` varsa gruplama **klasör kimliğine** göre yapılır; aynı adlı iki
 * farklı klasör tek albümde birleşmez.
 */
function resolveAlbum(item: Item, metadata: ReadonlyMap<string, MusicAlbum>): ResolvedAlbum {
  const id = item.albumId ?? null;
  const meta = id === null ? undefined : metadata.get(id);
  const artist = meta?.artist ?? artistOf(item);
  const title = meta?.title ?? albumOf(item);
  const artistKey = artist.toLocaleLowerCase('tr');
  return {
    id,
    key: id ?? `${artistKey}\u0000${title.toLocaleLowerCase('tr')}`,
    title,
    artist,
    year: meta?.year ?? item.year,
    coverItemId: meta?.coverItemId ?? item.id,
    coverThumbnailUrl: meta?.coverThumbnailUrl ?? item.thumbnailUrl ?? null,
  };
}

/**
 * Müzik kütüphanesindeki parçaları sanatçı ve albüm altında gruplar.
 *
 * Albüm kimliği sunucudan gelir (`Item.albumId`) ve **klasör yolundan**
 * türetilir. Etiketler eksik olsa bile her klasör kendi albümü olur; albüm
 * adı/sanatçısı kullanıcı düzenlemesinden (albüm künyesinden) gelir.
 *
 * `albumViews` verilmezse (eski sunucu) etiket tabanlı gruplama korunur.
 */
export function groupMusicLibraryItems(
  items: Item[],
  albumViews: readonly MusicAlbum[] = [],
): MusicLibraryArtistGroup[] {
  const artists = new Map<string, MusicLibraryArtistGroup>();
  const albums = new Map<string, MusicLibraryAlbumGroup>();
  const metadata = new Map(albumViews.map((album) => [album.id, album]));

  for (const item of items) {
    const resolved = resolveAlbum(item, metadata);
    const artistKey = resolved.artist.toLocaleLowerCase('tr');

    let artist = artists.get(artistKey);
    if (artist === undefined) {
      artist = {
        key: artistKey,
        name: resolved.artist,
        coverItemId: resolved.coverItemId,
        coverThumbnailUrl: resolved.coverThumbnailUrl,
        albumCount: 0,
        trackCount: 0,
        albums: [],
      };
      artists.set(artistKey, artist);
    }
    artist.trackCount += 1;
    if (artist.coverThumbnailUrl === null) {
      artist.coverThumbnailUrl = resolved.coverThumbnailUrl;
      if (resolved.coverThumbnailUrl !== null) artist.coverItemId = resolved.coverItemId;
    }

    let album = albums.get(resolved.key);
    if (album === undefined) {
      album = {
        key: resolved.key,
        id: resolved.id,
        title: resolved.title,
        year: resolved.year,
        coverItemId: resolved.coverItemId,
        coverThumbnailUrl: resolved.coverThumbnailUrl,
        tracks: [],
      };
      albums.set(resolved.key, album);
      artist.albums.push(album);
      artist.albumCount += 1;
    }
    album.tracks.push(item);
  }

  for (const artist of artists.values()) {
    artist.albums.sort(
      (a, b) =>
        (a.year ?? Number.MAX_SAFE_INTEGER) - (b.year ?? Number.MAX_SAFE_INTEGER) ||
        compareText(a.title, b.title),
    );
    for (const album of artist.albums) {
      album.tracks.sort(
        (a, b) =>
          (a.music?.discNumber ?? Number.MAX_SAFE_INTEGER) -
            (b.music?.discNumber ?? Number.MAX_SAFE_INTEGER) ||
          (a.music?.trackNumber ?? Number.MAX_SAFE_INTEGER) -
            (b.music?.trackNumber ?? Number.MAX_SAFE_INTEGER) ||
          compareText(a.title, b.title),
      );
    }
  }

  return [...artists.values()].sort((a, b) => compareText(a.name, b.name));
}
